import Button from "@/components/Button";
import Progress from "@/components/Progress";
import Tag from "@/components/Tag/Tag";
import { useNavigate } from "@/router";
import type { ProgressCardProps } from "./ProgressCard.types";

export default function ProgressCard({
  title,
  rowLabel,
  percent,
  completed,
  total,
  detailLabel = "on target",
  detailText,
  statusLabel,
  statusTagType = "success",
  reportLabel = "View Report",
  reportTo,
  onReportClick,
  progressTone = "primary",
  variant = "pacing",
  targetPercent,
  valueLabel,
  testId = "progress-card",
  reportTestId = "progress-card-report"
}: ProgressCardProps) {
  const navigate = useNavigate();
  const hasReportAction = !!(reportTo || onReportClick);
  const clampedPercent = Math.min(100, Math.max(0, percent));
  const showTargetLine = variant === "target" && targetPercent != null;
  const clampedTargetPercent = targetPercent == null ? null : Math.min(100, Math.max(0, targetPercent));
  const progressValue = (
    <Progress variant="linear" value={clampedPercent / 100} tone={progressTone} className="w-full" />
  );

  const handleReportClick = () => {
    if (onReportClick) {
      onReportClick();
      return;
    }

    if (reportTo) {
      navigate(reportTo);
    }
  };

  return (
    <section
      data-testid={testId}
      className="rounded-lg border border-sys-brand-outline bg-sys-brand-surface overflow-hidden"
    >
      <div className="flex items-center justify-between gap-2 w-full p-4 pb-3">
        <h2 className="text-title-medium-bold text-sys-brand-on-surface">{title}</h2>
      </div>
      <div className="px-4 pb-4">
        <div className="flex items-center justify-between">
          <span className="text-body-medium-semibold text-sys-brand-on-surface-variant">{rowLabel}</span>
          <span className="text-body-large-bold text-sys-brand-on-surface">{valueLabel ?? `${clampedPercent}%`}</span>
        </div>
        {variant === "target" ? (
          <div className="relative mt-2 h-4">
            <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2">
              <Progress variant="linear" value={clampedPercent / 100} tone={progressTone} className="h-full w-full" />
            </div>
            {showTargetLine && clampedTargetPercent != null ? (
              <span
                className="pointer-events-none absolute top-0 z-10 h-4 w-0.5 rounded-full bg-sys-brand-on-surface ring-1 ring-sys-brand-surface"
                style={{ left: `${clampedTargetPercent}%`, transform: "translateX(-50%)" }}
                data-testid={`${testId}-target-line`}
              />
            ) : null}
          </div>
        ) : (
          <div className="mt-2">{progressValue}</div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-body-small text-sys-brand-on-surface-variant">
            {detailText ?? `${completed.toLocaleString()} of ${total.toLocaleString()} ${detailLabel}`}
          </p>
          {statusLabel ? (
            <Tag tagType={statusTagType} tagStyle="standard" size="small">
              {statusLabel}
            </Tag>
          ) : null}
        </div>
      </div>
      {hasReportAction ? (
        <div className="border-t border-sys-brand-surface-container-highest px-4 py-2 text-center">
          <Button variant="text" onClick={handleReportClick} testId={reportTestId}>
            {reportLabel}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
